import { inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot
} from "@angular/router";
import { Auth } from "aws-amplify";
import { of } from "rxjs";
import { catchError } from "rxjs/operators";
import { UserBackend } from "../backend/api/user.backend";
import { User } from "../backend/models/user/user";
import { BootstrapService } from "../modules/bootstrap/bootstrap.service";
import { sntLog } from "src/app/common/logger";
import { maintenance } from "src/app/support/maintenance";
import { SettingsService } from "src/app/services/settings.service";
import { HttpErrorResponse } from "@angular/common/http";
import { setUserForErrorReporting } from "src/app/support/error-reporting";
import { SoftwareProduct } from "src/app/support/product";

const logger = sntLog("ProvideUserGuard");

@Injectable()
export class ProvideUserGuard {
  private settingsService = inject(SettingsService);

  constructor(
    private bootstrapService: BootstrapService,
    private userBackend: UserBackend,
    private router: Router
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    logger.info("canActivate");

    logger.debug(`[ProvideUserGuard] canActivate`);

    if (
      this.bootstrapService.getCurrentProduct() === SoftwareProduct.BOTMASTER
    ) {
      this.settingsService.setUiSettings({ theme: "light" });
      return true;
    }

    let currentUser: User | null;
    try {
      currentUser = await this.userBackend.loadMe().toPromise();

      setUserForErrorReporting({
        id: currentUser.id,
        email: currentUser.email,
        username: currentUser.username
      });

      this.bootstrapService.setCurrentUser(currentUser!);

      this.settingsService.setUiSettings(currentUser.uiSettings);

      return true;
    } catch (ex) {
      const httpErrorResponse = ex as HttpErrorResponse;
      if (httpErrorResponse.status === 404) {
        const authenticaedUser = await Auth.currentAuthenticatedUser();
        await this.userBackend
          .create({
            email: authenticaedUser.attributes.email,
            enabled: true,
            firstName: authenticaedUser.attributes.name,
            lastName: authenticaedUser.attributes.family_name,
            username: authenticaedUser.attributes.email
          })
          .toPromise();

        logger.info("user created");

        currentUser = await this.userBackend
          .loadMe()
          .pipe(catchError(() => of(null)))
          .toPromise();

        this.bootstrapService.setCurrentUser(currentUser!);

        this.settingsService.setUiSettings(currentUser!.uiSettings);

        return true;
      }

      maintenance(this.router, {});

      return false;
    }
  }
}
